// Integração com a API do University (TalentLMS) — https://university.ciandt.com
//
// Pattern oficialmente usado pela POC (`poc-anthroctopus/src/lib/datasource/talent-api`):
//   1. GET /users — TalentLMS devolve TODOS os usuários em UMA resposta
//      (sem paginação, ~20k usuários, ~15MB JSON). Não passe `?page=...`
//      nem `?page_size=...`: a API responde 400 "Invalid arguments provided".
//   2. GET /courses/id:{N} — devolve `users[]` apenas com `id` (string) e
//      `completion_percentage`. O `login` precisa ser resolvido via userMap.
//   3. Combinamos para devolver `CertificationData` por login: progresso
//      fracionário em cada trilha do catálogo + timestamp da primeira
//      certificação em trilha elegível ao prêmio (regulamento 8.2).
//
// O service é data-driven a partir de `src/lib/trilhas.ts` (IDs hardcoded por
// trilha, validados pelo smoke test): adicionar uma trilha nova consiste em
// estender o catálogo, sem mudar este arquivo. `fetchAllCourses` permanece
// exportado para uso do smoke test / UI de auditoria — não é mais necessário
// para resolver trilhas.

import { LmsUser, LmsCourseUser, LmsCourseResponse, CertificationData } from "../types";
import { TRILHAS, TrilhaId, trilhaProgress, allCourseIds, mapTrilhas } from "../lib/trilhas";

const BASE_URL = "/university-api";

interface LmsCourseRaw {
  id: string;
  name: string;
}

export interface LmsCourse {
  id: number;
  name: string;
}

/**
 * TalentLMS retorna `completed_on` como string US ("MM/DD/YYYY, HH:mm:ss") e
 * `completed_on_timestamp` como string com unix-seconds. Preferimos o timestamp.
 */
function lmsDateToIso(u: LmsCourseUser): string | null {
  const ts =
    typeof u.completed_on_timestamp === "string"
      ? Number(u.completed_on_timestamp)
      : u.completed_on_timestamp;
  if (typeof ts === "number" && Number.isFinite(ts) && ts > 0) {
    return new Date(ts * 1000).toISOString();
  }
  const match = u.completed_on?.match(/^(\d{2})\/(\d{2})\/(\d{4}),\s*(\d{2}):(\d{2}):(\d{2})$/);
  if (!match) return null;
  const [, mm, dd, yyyy, h, m, s] = match;
  return new Date(`${yyyy}-${mm}-${dd}T${h}:${m}:${s}Z`).toISOString();
}

/**
 * Normaliza o `login` retornado pelo LMS para a forma curta usada como chave
 * de junção com o CSV de colaboradores. Alguns usuários antigos têm o login
 * gravado como o email completo (ex.: "login@ciandt.com" em vez de só
 * "login"); sem o strip, o `certMap` fica indexado pela forma email e o
 * lookup pela forma curta (vinda do CSV) falha silenciosamente, zerando o
 * progresso da pessoa no dashboard.
 */
function normalizeLmsLogin(raw: string): string {
  return raw.toLowerCase().replace(/@ciandt\.com$/, "");
}

async function getJson<T>(path: string): Promise<T> {
  const resp = await fetch(`${BASE_URL}${path}`, {
    headers: { Accept: "application/json" },
  });
  if (!resp.ok) {
    let detail = "";
    try {
      const body = (await resp.json()) as { error?: { message?: string } };
      detail = body?.error?.message ?? "";
    } catch {
      /* not JSON */
    }
    throw new Error(`University API ${resp.status} on ${path}${detail ? `: ${detail}` : ""}`);
  }
  return (await resp.json()) as T;
}

// ---------------------------------------------------------------------------
// Cache do userMap (id -> login)
// ---------------------------------------------------------------------------
//
// O endpoint /users do TalentLMS devolve TODOS os ~20k usuários em uma única
// resposta de ~15MB; não há paginação nem filtro server-side, e o RTT chega a
// passar de 60s. Persistir o `id -> login` no `localStorage` resolve o caso
// comum (recargas / hot reload / abrir dashboard de novo): o mapeamento muda
// pouco (admissões/demissões), enquanto os dados que mudam diariamente
// (completions) NÃO são cacheados.
//
// TTL alto demais => login de pessoa nova não aparece até expirar; TTL baixo
// demais => perdemos o ganho. 12h pega o caso "abre de manhã, deixa aberto o
// dia inteiro, abre de novo no dia seguinte" sem stale relevante.

const USER_MAP_CACHE_KEY = "agentic-league:lms-user-map:v1";
const USER_MAP_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h

interface UserMapCacheEntry {
  ts: number;
  entries: Array<[string, string]>;
}

function readUserMapCache(): Map<string, string> | null {
  try {
    const raw = localStorage.getItem(USER_MAP_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserMapCacheEntry;
    if (!parsed?.ts || !Array.isArray(parsed.entries)) return null;
    if (Date.now() - parsed.ts > USER_MAP_CACHE_TTL_MS) return null;
    return new Map(parsed.entries);
  } catch {
    return null;
  }
}

function writeUserMapCache(map: Map<string, string>): void {
  try {
    const payload: UserMapCacheEntry = { ts: Date.now(), entries: [...map.entries()] };
    localStorage.setItem(USER_MAP_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage cheio ou modo privado: tudo bem, segue sem cache.
  }
}

/** Limpa o cache do userMap — útil para um botão "atualizar" / admin. */
export function clearUsersMapCache(): void {
  try {
    localStorage.removeItem(USER_MAP_CACHE_KEY);
  } catch {
    /* noop */
  }
}

// Dedup de chamadas concorrentes no mesmo tick / sessão.
let inflightUsers: Promise<LmsUser[]> | null = null;

/** Faz o GET /users uma vez por sessão (dedup) — derivações de map/directory
 *  consomem o resultado. */
async function fetchUsersRaw(opts: { forceRefresh?: boolean } = {}): Promise<LmsUser[]> {
  if (!opts.forceRefresh && inflightUsers) return inflightUsers;
  inflightUsers = (async () => getJson<LmsUser[]>(`/users`))().finally(() => {
    inflightUsers = null;
  });
  return inflightUsers;
}

/**
 * id (string) -> login. TalentLMS devolve a lista completa em UMA chamada;
 * passar `?page=...` ou `?page_size=...` aqui dá HTTP 400.
 *
 * Cache layers (em ordem de preferência):
 *   1. Promessa em vôo nesta sessão (dedup).
 *   2. localStorage (TTL 12h).
 *   3. Rede.
 */
export async function fetchUsersMap(
  opts: { forceRefresh?: boolean } = {},
): Promise<Map<string, string>> {
  if (!opts.forceRefresh) {
    const cached = readUserMapCache();
    if (cached) return cached;
  }

  const users = await fetchUsersRaw(opts);
  const map = new Map<string, string>();
  for (const u of users) {
    if (u?.id && u?.login) map.set(String(u.id), normalizeLmsLogin(u.login));
  }
  writeUserMapCache(map);
  return map;
}

// ---------------------------------------------------------------------------
// User directory (login -> { firstName, lastName })
// ---------------------------------------------------------------------------
//
// Usado pelo data.store para enriquecer `Colaborador.name` com o nome completo
// do LMS. A API de pessoas devolve `Full Name`, mas o LMS pode ter formas
// canônicas diferentes (capitalização, nomes compostos); o enriquecimento LMS
// permanece como source de verdade para o nome exibido no dashboard.
// Possível limpeza futura: avaliar se a API de pessoas passa a ser suficiente.

export interface LmsUserName {
  firstName: string;
  lastName: string;
}

const USER_DIR_CACHE_KEY = "agentic-league:lms-user-directory:v1";
const USER_DIR_CACHE_TTL_MS = 12 * 60 * 60 * 1000;

interface UserDirCacheEntry {
  ts: number;
  entries: Array<[string, LmsUserName]>;
}

function readUserDirCache(): Map<string, LmsUserName> | null {
  try {
    const raw = localStorage.getItem(USER_DIR_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserDirCacheEntry;
    if (!parsed?.ts || !Array.isArray(parsed.entries)) return null;
    if (Date.now() - parsed.ts > USER_DIR_CACHE_TTL_MS) return null;
    return new Map(parsed.entries);
  } catch {
    return null;
  }
}

function writeUserDirCache(map: Map<string, LmsUserName>): void {
  try {
    const payload: UserDirCacheEntry = { ts: Date.now(), entries: [...map.entries()] };
    localStorage.setItem(USER_DIR_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* noop */
  }
}

export function clearUsersDirectoryCache(): void {
  try {
    localStorage.removeItem(USER_DIR_CACHE_KEY);
  } catch {
    /* noop */
  }
}

/** login (lowercase) -> { firstName, lastName }. Skipa entradas sem login ou
 *  sem nenhum dos dois nomes. */
export async function fetchUsersDirectory(
  opts: { forceRefresh?: boolean } = {},
): Promise<Map<string, LmsUserName>> {
  if (!opts.forceRefresh) {
    const cached = readUserDirCache();
    if (cached) return cached;
  }

  const users = await fetchUsersRaw(opts);
  const directory = new Map<string, LmsUserName>();
  for (const u of users) {
    if (!u?.login) continue;
    const firstName = u.first_name?.trim() ?? "";
    const lastName = u.last_name?.trim() ?? "";
    if (!firstName && !lastName) continue;
    directory.set(normalizeLmsLogin(u.login), { firstName, lastName });
  }
  writeUserDirCache(directory);
  return directory;
}

// ---------------------------------------------------------------------------
// Cache do catálogo de cursos
// ---------------------------------------------------------------------------
//
// Mesmo padrão do userMap: o /courses devolve toda a lista em uma chamada, e
// o catálogo muda lentamente (novos cursos saem do forno raramente). 12h de
// TTL casa com o ciclo de uso diário do dashboard.

const COURSE_CATALOG_CACHE_KEY = "agentic-league:lms-course-catalog:v1";
const COURSE_CATALOG_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h

interface CourseCatalogCacheEntry {
  ts: number;
  courses: LmsCourse[];
}

function readCourseCatalogCache(): LmsCourse[] | null {
  try {
    const raw = localStorage.getItem(COURSE_CATALOG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CourseCatalogCacheEntry;
    if (!parsed?.ts || !Array.isArray(parsed.courses)) return null;
    if (Date.now() - parsed.ts > COURSE_CATALOG_CACHE_TTL_MS) return null;
    return parsed.courses;
  } catch {
    return null;
  }
}

function writeCourseCatalogCache(courses: LmsCourse[]): void {
  try {
    const payload: CourseCatalogCacheEntry = { ts: Date.now(), courses };
    localStorage.setItem(COURSE_CATALOG_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* noop */
  }
}

export function clearCourseCatalogCache(): void {
  try {
    localStorage.removeItem(COURSE_CATALOG_CACHE_KEY);
  } catch {
    /* noop */
  }
}

let inflightCatalog: Promise<LmsCourse[]> | null = null;

export async function fetchAllCourses(opts: { forceRefresh?: boolean } = {}): Promise<LmsCourse[]> {
  if (!opts.forceRefresh) {
    const cached = readCourseCatalogCache();
    if (cached) return cached;
    if (inflightCatalog) return inflightCatalog;
  }

  inflightCatalog = (async () => {
    const raw = await getJson<LmsCourseRaw[]>(`/courses`);
    const courses: LmsCourse[] = raw.map((c) => ({ id: Number(c.id), name: c.name }));
    writeCourseCatalogCache(courses);
    return courses;
  })().finally(() => {
    inflightCatalog = null;
  });

  return inflightCatalog;
}

/** Versão crua: lmsId (string) -> ISO timestamp da conclusão. Adiamos a
 *  resolução para login para podermos disparar essa chamada EM PARALELO ao
 *  /users (que é o gargalo de 15MB). */
async function fetchCourseCompletionsRaw(courseId: number): Promise<Map<string, string | null>> {
  const data = await getJson<LmsCourseResponse>(`/courses/id:${courseId}`);
  const result = new Map<string, string | null>();
  for (const u of data.users ?? []) {
    if (u.completion_percentage !== "100") continue;
    result.set(String(u.id), lmsDateToIso(u));
  }
  return result;
}

export async function fetchAllCertifications(): Promise<Map<string, CertificationData>> {
  // IDs vêm direto do catálogo estático de trilhas — não precisamos mais
  // resolver matchers contra /courses. /users e /courses/id:{N} continuam em
  // paralelo: o gargalo é /users (~15MB).
  const userMapPromise = fetchUsersMap();
  const courseIds = allCourseIds(TRILHAS);

  const courseResults = await Promise.all(
    courseIds.map((id) =>
      fetchCourseCompletionsRaw(id).then(
        (m) => [id, m] as const,
        (err: unknown) => {
          console.warn(`[universityApi] curso ${id} falhou:`, err);
          return [id, new Map<string, string | null>()] as const;
        },
      ),
    ),
  );

  const userMap = await userMapPromise;

  // login -> (courseId -> completedAt | null). Os cursos vieram com lmsId
  // cru (string); resolvemos para login aqui, agora que o userMap chegou.
  const perLogin = new Map<string, Map<number, string | null>>();
  for (const [courseId, completions] of courseResults) {
    for (const [lmsId, completedAt] of completions) {
      const login = userMap.get(lmsId);
      if (!login) continue;
      let bucket = perLogin.get(login);
      if (!bucket) {
        bucket = new Map();
        perLogin.set(login, bucket);
      }
      bucket.set(courseId, completedAt);
    }
  }

  const result = new Map<string, CertificationData>();

  for (const [login, courseCompletions] of perLogin) {
    const certifiedSet = new Set<number>(courseCompletions.keys());

    const trilhaProgressMap: Record<TrilhaId, number> = mapTrilhas((trilha) =>
      trilhaProgress(trilha, certifiedSet),
    );

    // Para cada trilha concluída a 100%, "data de conclusão da trilha" =
    // max(completedAt dos cursos resolvidos). Guardamos essa data por trilha
    // em `trilhaCertifiedAt` (usada pelo gráfico de evolução — total de
    // certificações, não só pessoas). Depois:
    //   certifiedAt       = min sobre TODAS as trilhas concluídas (Fase Pocket §7.1).
    //   prizeCertifiedAt  = min sobre as trilhas elegíveis ao prêmio (Fase Pocket §7.2).
    // In Fase Pocket every trilha has countsForPrizeEligibility=true,
    // so prizeCertifiedAt === certifiedAt in practice.
    let certifiedAt: string | null = null;
    let prizeCertifiedAt: string | null = null;
    const trilhaCertifiedAtMap: Record<TrilhaId, string | null> = mapTrilhas(() => null);
    for (const t of TRILHAS) {
      if ((trilhaProgressMap[t.id] ?? 0) < 1) continue;
      let trilhaCompletedAt: string | null = null;
      for (const slot of t.courseSlots) {
        for (const cid of slot.courseIds) {
          const completedAt = courseCompletions.get(cid);
          if (!completedAt) continue;
          if (!trilhaCompletedAt || new Date(completedAt) > new Date(trilhaCompletedAt)) {
            trilhaCompletedAt = completedAt;
          }
        }
      }
      // legacyFullCredit: quando o 100% veio do crédito retroativo (courseSlots
      // atuais sem completion), a data vem dos cursos legados.
      if (!trilhaCompletedAt && t.legacyFullCredit) {
        for (const cid of t.legacyFullCredit) {
          const completedAt = courseCompletions.get(cid);
          if (!completedAt) continue;
          if (!trilhaCompletedAt || new Date(completedAt) > new Date(trilhaCompletedAt)) {
            trilhaCompletedAt = completedAt;
          }
        }
      }
      if (!trilhaCompletedAt) continue;
      trilhaCertifiedAtMap[t.id] = trilhaCompletedAt;
      if (!certifiedAt || new Date(trilhaCompletedAt) < new Date(certifiedAt)) {
        certifiedAt = trilhaCompletedAt;
      }
      if (
        t.countsForPrizeEligibility &&
        (!prizeCertifiedAt || new Date(trilhaCompletedAt) < new Date(prizeCertifiedAt))
      ) {
        prizeCertifiedAt = trilhaCompletedAt;
      }
    }

    result.set(login, {
      trilhaProgress: trilhaProgressMap,
      certifiedCourseIds: Array.from(certifiedSet).sort((a, b) => a - b),
      certifiedAt,
      prizeCertifiedAt,
      trilhaCertifiedAt: trilhaCertifiedAtMap,
    });
  }

  return result;
}

export type { LmsUser, LmsCourseUser };
export { TRILHAS };
